import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Tippy from '@tippy.js/react';
import {
  ApButtonSecondary,
  ApIcon,
  ApLoaderDefault,
  ApToggleSwitch,
  snack,
} from '@alixpartners/ui-components';
import { NavigationWrapper } from '../NavigationWrapper';
import { DeleteAllDataModal } from '../../../components/DeleteAllDataModal';
import {
  createSharedPowerBiReport,
  fetchPermissions,
  fetchPowerBiReports,
  fetchPowerBiReportsList,
  fetchSettings,
  removeSharedPowerBiReport,
  selectPermissions,
  selectPowerBiReports,
  selectSettings,
  useAppDispatch,
  useAppSelector,
} from '../../../state';
import { IReport } from '../../../types';
import { fetchPowerBiPBIDSFile } from '../../../utils/api';
import { downloadObjectAsJson } from '../../../utils/common';
import {
  PageWrapper,
  PageTitleBlock,
  PageTitle,
  PageSubtitle,
  ContentRow,
  SidebarCard,
  SidebarTitle,
  SidebarList,
  SidebarItem,
  MainCard,
  MainCardHeader,
  SectionTitle,
  TableWrapper,
  TableHeaderRow,
  ColHeader,
  TableRow,
  FileNameCell,
  FileIconContainer,
  ReportLink,
  CellText,
  ActionsCell,
  TableLoadingRow,
  TableEmptyRow,
  BuildContent,
  BuildSection,
  BuildSectionText,
  BuildSectionHeading,
  BuildSectionBody,
  BuildSectionMeta,
  BuildMetaLabel,
  BuildMetaValue,
  BuildLink,
  HeaderActions,
  TertiaryButton,
  ShareToggleGroup,
  ShareToggleLabel,
  RowActionDivider,
  ViewReportsButton,
} from './Reports.styles';

type TReportTab = 'all' | 'build';

const SIDEBAR_TABS: Array<{ key: TReportTab; label: string }> = [
  { key: 'all', label: 'All reports' },
  { key: 'build', label: 'Build Reports' },
];

const PAGE_TITLES: Record<TReportTab, { title: string; subtitle: string }> = {
  all: {
    title: 'Explore your reports',
    subtitle:
      'View and analyze your data based on the latest available version.',
  },
  build: {
    title: 'Build Reports',
    subtitle:
      'Set up your Power BI environment to start building and sharing reports. This process connects your workspace to the platform, so your mapped data is ready to use in Power BI Desktop and can be shared with clients.',
  },
};

export const Reports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { reports, loading } = useAppSelector(selectPowerBiReports);
  const settingsData = useAppSelector(selectSettings);
  const permissionsData = useAppSelector(selectPermissions);
  const sharePBReportEnabled =
    permissionsData.permissions?.sharePBReportEnabled ?? false;
  const [activeTab, setActiveTab] = useState<TReportTab>('all');
  const [unshareReport, setUnshareReport] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [busyRow, setBusyRow] = useState<{
    name: string;
    action: 'share' | 'refresh';
  } | null>(null);

  useEffect(() => {
    dispatch(fetchPowerBiReports());
    if (!permissionsData.permissions) {
      dispatch(fetchPermissions());
    }
  }, []);

  useEffect(() => {
    if (
      !settingsData.settings &&
      !settingsData.loading &&
      !settingsData.failed
    ) {
      dispatch(fetchSettings());
    }
  }, []);

  const handleViewReports = () => {
    window.open(`${window.location.origin}/client-report-site`, '_blank');
  };
  const handleRefreshReport = async (id: string, rowName: string) => {
    setBusyRow({ name: rowName, action: 'refresh' });
    try {
      await dispatch(createSharedPowerBiReport(id)).unwrap();
      await dispatch(fetchPowerBiReportsList()).unwrap();
      snack.success({
        message: 'Report has been updated.',
      });
    } catch (error) {
      snack.error({
        message: 'Error updating report',
      });
    } finally {
      setBusyRow(null);
    }
  };
  const handleShareReport = async (id: string, rowName: string) => {
    setBusyRow({ name: rowName, action: 'share' });
    try {
      await dispatch(createSharedPowerBiReport(id)).unwrap();
      await dispatch(fetchPowerBiReportsList()).unwrap();
      snack.success({
        message: 'Report has been shared.',
      });
    } catch (error) {
      snack.error({
        message:
          error instanceof Error && error.message === 'Operation failed'
            ? 'Sharing failed on the server. Please try again.'
            : 'Error sharing report',
      });
    } finally {
      setBusyRow(null);
    }
  };
  const handleUnshareReport = async (id: string, rowName: string) => {
    setBusyRow({ name: rowName, action: 'share' });
    try {
      await dispatch(removeSharedPowerBiReport(id)).unwrap();
      await dispatch(fetchPowerBiReportsList()).unwrap();
      snack.success({
        message: 'Report has been un-shared.',
      });
    } catch (error) {
      snack.error({
        message: 'Error un-sharing report',
      });
    } finally {
      setBusyRow(null);
    }
  };
  const handleDownloadPbid = async () => {
    try {
      const pbidsFile = await fetchPowerBiPBIDSFile();
      downloadObjectAsJson(
        pbidsFile.data,
        'PowerBISQLServerConnection',
        'pbids'
      );
    } catch (error) {
      snack.error({
        message: 'Error loading pbids file',
      });
    }
  };

  const sectionLabel = 'All reports';
  const groupedReports = (reports ?? []).reduce(
    (acc: Record<string, IReport[]>, report) => {
      const name = report.displayName || report.reportName;
      acc[name] = [...(acc[name] ?? []), report];
      return acc;
    },
    {}
  );
  const rows = Object.keys(groupedReports).map((name) => {
    const entries = groupedReports[name];
    const unsharedEntry = entries.find((entry) => !entry.isShared);
    const sharedEntry = entries.find((entry) => entry.isShared);

    return {
      name,
      unsharedEntry,
      sharedEntry,
      displayEntry: unsharedEntry ?? entries[0],
    };
  });
  const { title, subtitle } = PAGE_TITLES[activeTab];

  return (
    <>
      <NavigationWrapper />
      <PageWrapper>
        <PageTitleBlock>
          <PageTitle>{title}</PageTitle>
          <PageSubtitle>{subtitle}</PageSubtitle>
        </PageTitleBlock>

        <ContentRow>
          <SidebarCard>
            <SidebarTitle>Reports</SidebarTitle>
            <SidebarList>
              {SIDEBAR_TABS.map((tab) => (
                <SidebarItem
                  key={tab.key}
                  isSelected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </SidebarItem>
              ))}
            </SidebarList>
          </SidebarCard>

          <MainCard>
            {activeTab === 'build' ? (
              <BuildContent>
                <BuildSection>
                  <BuildSectionText>
                    <BuildSectionHeading>
                      1. Download and open the report file
                    </BuildSectionHeading>
                    <BuildSectionBody>
                      Download the PBID file and open it in Power BI Desktop
                      (ETS VDI). This will automatically connect you to the
                      workspace. Build your reports and publish them when
                      you&apos;re ready to share.
                    </BuildSectionBody>
                    {settingsData.settings?.powerBiWorkspaceName && (
                      <BuildSectionMeta>
                        <BuildMetaLabel>WORKSPACE NAME:</BuildMetaLabel>
                        <BuildMetaValue>
                          {settingsData.settings.powerBiWorkspaceName}
                        </BuildMetaValue>
                      </BuildSectionMeta>
                    )}
                  </BuildSectionText>
                  <ApButtonSecondary
                    iconName="download"
                    small
                    onClick={handleDownloadPbid}
                  >
                    Download PBID File
                  </ApButtonSecondary>
                </BuildSection>

                <BuildSection>
                  <BuildSectionText>
                    <BuildSectionHeading>
                      2. Access to Power BI Workspace
                    </BuildSectionHeading>
                    <BuildSectionBody>
                      Make sure you have a Power BI Pro license and access to
                      your workspace. If it&apos;s your first time, log in to
                      activate your license. You can request access if needed
                      using{' '}
                      {settingsData.settings?.powerBiWorkspaceUrl ? (
                        <BuildLink
                          href={settingsData.settings.powerBiWorkspaceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          this link.
                        </BuildLink>
                      ) : (
                        'this link.'
                      )}
                    </BuildSectionBody>
                  </BuildSectionText>
                  <ApButtonSecondary
                    small
                    disabled={!settingsData.settings?.powerBiWorkspaceUrl}
                    onClick={() =>
                      window.open(
                        settingsData.settings?.powerBiWorkspaceUrl,
                        '_blank'
                      )
                    }
                  >
                    Open Power BI Workspace
                  </ApButtonSecondary>
                </BuildSection>
              </BuildContent>
            ) : (
              <>
                <MainCardHeader>
                  <SectionTitle>{sectionLabel}</SectionTitle>
                  <HeaderActions>
                    <ViewReportsButton onClick={handleViewReports}>
                      View reports
                    </ViewReportsButton>
                  </HeaderActions>
                </MainCardHeader>

                <TableWrapper>
                  <TableHeaderRow>
                    <ColHeader width={248}>
                      Report Name
                      <ApIcon
                        iconName="unfold_more"
                        material
                        iconSize={16}
                        iconColor="#737373"
                      />
                    </ColHeader>
                    <ColHeader width={120}>
                      Created by / Date
                      <ApIcon
                        iconName="unfold_more"
                        material
                        iconSize={16}
                        iconColor="#737373"
                      />
                    </ColHeader>
                    <ColHeader width={148}>
                      Share by / Date
                      <ApIcon
                        iconName="unfold_more"
                        material
                        iconSize={16}
                        iconColor="#737373"
                      />
                    </ColHeader>
                    <ColHeader width={264} alignRight>
                      Actions
                    </ColHeader>
                  </TableHeaderRow>

                  {loading && (
                    <TableLoadingRow>Loading reports...</TableLoadingRow>
                  )}
                  {!loading && rows.length === 0 && (
                    <TableEmptyRow>No reports available.</TableEmptyRow>
                  )}
                  {rows.map((row) => (
                    <TableRow key={row.name}>
                      <FileNameCell>
                        <FileIconContainer>
                          <ApIcon
                            iconName="outline_insert_drive_file"
                            iconSize={20}
                          />
                        </FileIconContainer>
                        <ReportLink
                          onClick={() =>
                            window.open(row.displayEntry.reportWebUrl, '_blank')
                          }
                        >
                          {row.name}
                        </ReportLink>
                      </FileNameCell>
                      <CellText width="120px">{'\u2014'}</CellText>
                      <CellText width="148px">
                        {row.sharedEntry?.sharedBy ? (
                          <>
                            {row.sharedEntry.sharedBy} on
                            <br />
                            {moment(row.sharedEntry.sharedDate).format(
                              'MM/DD/YYYY'
                            )}
                          </>
                        ) : (
                          <>{'\u2014'}</>
                        )}
                      </CellText>
                      <ActionsCell>
                        <ShareToggleGroup>
                          {busyRow?.name === row.name &&
                          busyRow.action === 'share' ? (
                            <ApLoaderDefault loaderSize="sm" />
                          ) : row.sharedEntry || sharePBReportEnabled ? (
                            <ApToggleSwitch
                              id={`share-toggle-${row.name}`}
                              checked={!!row.sharedEntry}
                              onChange={() =>
                                row.sharedEntry
                                  ? setUnshareReport({
                                      id: row.sharedEntry.id,
                                      name: row.name,
                                    })
                                  : row.unsharedEntry &&
                                    handleShareReport(
                                      row.unsharedEntry.id,
                                      row.name
                                    )
                              }
                            />
                          ) : (
                            <Tippy
                              arrow={true}
                              placement="top"
                              animation="fade"
                              boundary="window"
                              interactive={true}
                              content="Only Approvers may share reports."
                            >
                              <div>
                                <ApToggleSwitch
                                  id={`share-toggle-${row.name}`}
                                  checked={false}
                                  disabled
                                />
                              </div>
                            </Tippy>
                          )}
                          <ShareToggleLabel>Share</ShareToggleLabel>
                        </ShareToggleGroup>
                        <RowActionDivider />
                        <TertiaryButton
                          type="button"
                          disabled={
                            !row.sharedEntry ||
                            !row.unsharedEntry ||
                            !sharePBReportEnabled ||
                            busyRow?.name === row.name
                          }
                          onClick={() =>
                            row.unsharedEntry &&
                            handleRefreshReport(row.unsharedEntry.id, row.name)
                          }
                        >
                          {busyRow?.name === row.name &&
                          busyRow.action === 'refresh' ? (
                            <ApLoaderDefault loaderSize="sm" />
                          ) : (
                            <ApIcon iconName="replay" iconSize={20} />
                          )}
                          Refresh
                        </TertiaryButton>
                      </ActionsCell>
                    </TableRow>
                  ))}
                </TableWrapper>
              </>
            )}
          </MainCard>
        </ContentRow>
      </PageWrapper>
      {unshareReport && (
        <DeleteAllDataModal
          onSubmit={() => {
            handleUnshareReport(unshareReport.id, unshareReport.name);
            setUnshareReport(null);
          }}
          onCancel={() => setUnshareReport(null)}
          actionPhrase="un-share report"
        />
      )}
    </>
  );
};

export default Reports;
